import { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useBookingRequests from '@/api/useBookingRequests.ts';
import useUpdateBookingStatus from '@/api/useUpdateBookingStatus.ts';
import useCreateReview from '@/api/useCreateReview.ts';
import { Button } from 'react-daisyui';
import dayjs from 'dayjs';
import Routes from '@/router/Routes.ts';
import ReviewModal from '@/components/modals/ReviewModal.tsx';
import DeclineBookingModal from '@/components/modals/DeclineBookingModal.tsx';
import { trackEvent } from '@/utils/analytics.ts';
import { formatCareType } from '@/utils/formatCareType.ts';

const BookingDetailsFullPage: FC = () => {
  const { t } = useTranslation();
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { data: bookings, isLoading, isError } = useBookingRequests();
  const { mutate: updateStatus, isPending: isUpdating } = useUpdateBookingStatus();
  const { mutate: createReview, isPending: isSubmittingReview } = useCreateReview();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  const booking = bookings?.find((b) => b.id === bookingId);

  const handleAccept = () => {
    if (booking) {
      updateStatus(
        { booking_id: booking.id, status: 'accepted' },
        { onSuccess: () => trackEvent({ name: 'booking_request_accepted' }) }
      );
    }
  };

  const handleDeclineConfirm = (reason: string) => {
    if (booking) {
      updateStatus(
        { booking_id: booking.id, status: 'declined', decline_reason: reason },
        { onSuccess: () => setIsDeclineModalOpen(false) }
      );
    }
  };

  const handleSubmitReview = (rating: number, comment: string) => {
    if (booking) {
      createReview(
        {
          booking_id: booking.id,
          reviewee_id: booking.carer_id,
          rating,
          comment,
        },
        {
          onSuccess: () => {
            setIsReviewModalOpen(false);
          },
        }
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-[#FFBE00]';
      case 'accepted':
        return 'text-[#00A96E]';
      case 'declined':
        return 'text-[#FF5861]';
      case 'completed':
        return 'text-[#00A96E]';
      case 'not_completed':
        return 'text-[#FF5861]';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'not_completed':
        return 'Not Completed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg mb-4" />
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError || !booking) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <button 
          onClick={() => navigate(Routes.BOOKING)} 
          className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer"
        >
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>
        <div className="text-red-500">{t('bookingsPage.notFound')}</div>
      </div>
    );
  }

  const showRateButton = booking.status === 'completed' && !booking.has_review && booking.is_requester;
  const showActionButtons = booking.status === 'pending' && !booking.is_requester;
  
  // Get the other user's ID (the one we want to message)
  const otherUserId = booking.is_requester ? booking.carer_id : booking.user_id;

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button 
          onClick={() => navigate(Routes.BOOKING)} 
          className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer flex-shrink-0"
        >
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>
        <Button
          onClick={() => navigate(Routes.MESSAGES_WITH_USER.replace(':userId', otherUserId))}
          className="bg-black text-white border-none hover:opacity-80 rounded-[12px] px-6 flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          {t('common.sendMessage')}
          <img src="/assets/message.svg" alt="message" className="w-5 h-5" />
        </Button>
      </div>

      <div className="bg-[#F6F6F4] rounded-xl p-4 sm:p-6 space-y-6">
        <div className="rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">{t('common.status')}</label>
          <div className="bg-white rounded-[120px] mt-2 px-4 py-2 inline-block">
            <p className={`text-sm font-bold ${getStatusColor(booking.status)}`}>
              {getStatusLabel(booking.status)}
            </p>
          </div>
        </div>

        <div className="rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">
            {booking.is_requester ? 'Carer' : t('tableHeaders.family')}
          </label>
          <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
            {booking.other_user_name}
          </p>
        </div>

        {booking.care_type && (
          <div className="rounded-lg">
            <label className="text-sm text-[#9CA3AF] block mb-1">{t('tableHeaders.careType')}</label>
            <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
              {formatCareType(booking.care_type)}
            </p>
          </div>
        )}

        <div className="rounded-lg">
          <label className="text-sm text-[#9CA3AF] block mb-1">{t('bookingsPage.dateTimeRequested')}</label>
          <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
            {booking.start_date
              ? `${dayjs(booking.start_date).format('ddd, DD MMM')} — ${booking.start_time || ''}${booking.end_time ? ` - ${booking.end_time}` : ''}`
              : 'Not specified'}
          </p>
        </div>

        {booking.notes && (
          <div>
            <label className="text-sm text-[#9CA3AF] block mb-1">{t('bookingsPage.notes')}</label>
            <p className="text-sm">{booking.notes}</p>
          </div>
        )}

        {booking.status === 'declined' && booking.decline_reason && (
          <div className="rounded-lg">
            <label className="text-sm text-[#9CA3AF] block mb-1">Reason for declining</label>
            <div className="flex items-center gap-2 mt-2">
              <span className="bg-[#FF586114] text-[#FF5861] text-sm font-semibold rounded-[120px] px-4 py-2 inline-block">
                {booking.decline_reason}
              </span>
            </div>
          </div>
        )}
      </div>

      {showActionButtons && (
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleAccept}
            disabled={isUpdating}
            className="flex-1 max-w-[160px] bg-[#00A96E] text-white border-none hover:opacity-80 rounded-[12px]"
          >
            {t('common.accept')}
          </Button>
          <Button
            onClick={() => setIsDeclineModalOpen(true)}
            disabled={isUpdating}
            className="flex-1  max-w-[160px] bg-[#FF5861] text-white border-none hover:opacity-80 rounded-[12px]"
          >
            {t('common.decline')}
          </Button>
        </div>
      )}

      {showRateButton && (
        <Button
          onClick={() => setIsReviewModalOpen(true)}
          className="w-full bg-black text-white border-none hover:opacity-80 rounded-[12px]"
        >
          {t('bookingsPage.rateCarer')}
        </Button>
      )}

      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        onSubmit={handleSubmitReview}
        carerName={booking.other_user_name}
        isSubmitting={isSubmittingReview}
      />

      <DeclineBookingModal
        isOpen={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        onConfirm={handleDeclineConfirm}
        familyName={booking.other_user_name}
        isLoading={isUpdating}
      />
    </div>
  );
};

export default BookingDetailsFullPage;
